const defaultTransformValue = (rawValue) => rawValue;
const defaultSerializeValue = (value) => value;

const isEmptySearchValue = (value) => {
	if (value === undefined || value === null || value === "") {
		return true;
	}

	if (Array.isArray(value)) {
		return value.length === 0;
	}

	return false;
};

const normalizeSerializedValue = (value) => {
	if (Array.isArray(value)) {
		return value.join(",");
	}

	if (value === undefined || value === null) {
		return "";
	}

	return String(value);
};

export const transformCommaSeparatedValues = (rawValue, itemTransformer = defaultTransformValue) => {
	if (!rawValue) return [];

	const values = String(rawValue)
		.split(",")
		.map((value) => value.trim())
		.filter(Boolean)
		.map((value) => itemTransformer(value));

	return values;
};

export const transformCommaSeparatedNumbers = (rawValue) => transformCommaSeparatedValues(rawValue, Number);

export const updateSearchParams = (search, changes = {}) => {
	const searchParams = new URLSearchParams(search || "");

	Object.entries(changes).forEach(([queryParam, rawValue]) => {
		if (isEmptySearchValue(rawValue)) {
			searchParams.delete(queryParam);
			return;
		}

		const serializedValue = normalizeSerializedValue(rawValue);
		if (!serializedValue) {
			searchParams.delete(queryParam);
			return;
		}

		searchParams.set(queryParam, serializedValue);
	});

	return searchParams.toString();
};

export const syncFiltersWithSearch = (search, filters = [], filterMappings = []) => {
	const filterMap = new Map(filters.map((filterItem) => [filterItem.key, filterItem]));
	const searchParams = new URLSearchParams(search || "");

	filterMappings.forEach((filterMapping) => {
		const legacyQueryParams = filterMapping.legacyQueryParams || [];
		legacyQueryParams.forEach((legacyQueryParam) => {
			if (legacyQueryParam !== filterMapping.queryParam) {
				searchParams.delete(legacyQueryParam);
			}
		});
	});

	const nextChanges = filterMappings.reduce((accumulator, filterMapping) => {
		const filterItem = filterMap.get(filterMapping.filterKey);
		if (!filterItem) {
			return accumulator;
		}

		const serializeValue = filterMapping.serializeValue || defaultSerializeValue;
		accumulator[filterMapping.queryParam] = serializeValue(filterItem.value, filterItem);
		return accumulator;
	}, {});

	return updateSearchParams(searchParams.toString(), nextChanges);
};

export const buildInitialFiltersFromSearch = (baseFilters, search, filterMappings = []) => {
	const searchParams = new URLSearchParams(search || "");
	let hasUrlFilters = false;

	const filters = baseFilters.map((filterItem) => {
		const filterMapping = filterMappings.find((mapping) => mapping.filterKey === filterItem.key);
		if (!filterMapping) {
			return filterItem;
		}

		const queryParams = [filterMapping.queryParam, ...(filterMapping.legacyQueryParams || [])];
		const rawValue = queryParams.reduce((foundValue, queryParam) => {
			if (foundValue !== null) {
				return foundValue;
			}

			return searchParams.get(queryParam);
		}, null);
		if (rawValue === null) {
			return filterItem;
		}

		hasUrlFilters = true;
		const transformValue = filterMapping.transformValue || defaultTransformValue;
		return {
			...filterItem,
			value: transformValue(rawValue),
		};
	});

	return {
		filters,
		hasUrlFilters,
	};
};
